import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  type ReactNode
} from "react"
import {
  createMemoryRouter,
  RouteObject,
  RouterProvider
} from "react-router-dom"

type RouterAdapterProps = {
  element: RouteObject["element"]
}

/**
 * Embedded Auth (Cover / license flows) must not own `window.history`.
 * A browser router here fights the main app router and can navigate to
 * `/auth/sign-in/?next=…` mid-conversion. Memory router keeps Auth's
 * useBlocker working without rewriting the Cover URL.
 *
 * Router is memoized + disposed (same lifecycle hygiene as WebAppRoutesForLegacy).
 * Latest `element` is provided via context so Auth props stay fresh without
 * recreating the router every render.
 */
const EmbeddedRouteElementContext = createContext<ReactNode>(null)

function EmbeddedRouteElement() {
  return <>{useContext(EmbeddedRouteElementContext)}</>
}

const RouterAdapter = ({ element }: RouterAdapterProps) => {
  const router = useMemo(
    () =>
      createMemoryRouter([
        {
          path: "*",
          element: <EmbeddedRouteElement />
        }
      ]),
    []
  )

  useEffect(() => () => router.dispose(), [router])

  return (
    <EmbeddedRouteElementContext.Provider value={element}>
      <RouterProvider router={router} />
    </EmbeddedRouteElementContext.Provider>
  )
}

export default RouterAdapter
